import { Link } from 'react-router-dom';

const steps = [
  {
    number: '01',
    icon: 'ri-building-line',
    title: 'Set Up Your Business Profile',
    subtitle: '2 minutes, once — then it remembers everything',
    description:
      'Start by telling AdForge about your business. Enter your business name, industry, target audience, key services or products, and unique selling points. This profile is saved permanently — every future ad generation uses this context automatically, so you never have to repeat yourself.',
    tips: [
      'Be specific about your audience: "homeowners aged 35–60 in suburban areas" beats "adults"',
      'Include your biggest offer or differentiator — the AI will work it into every ad',
      'Add your tone of voice: professional, friendly, urgent, casual, etc.',
    ],
    image: 'https://storage.readdy-site.link/project_files/b82d3669-b3d5-44e8-8c89-634acf8d0274/dfdc8f98-5d5d-4dcf-8dff-e438ad3c0faf_business_profile_img.png?v=6882c5ce873f93c23005fd85d5ad9505',
    color: 'orange',
    accent: 'bg-orange-50 text-orange-500 ring-orange-200',
  },
  {
    number: '02',
    icon: 'ri-settings-3-line',
    title: 'Choose Your Platform & Campaign Goal',
    subtitle: 'Facebook, Instagram, Google, TikTok, or LinkedIn',
    description:
      'Select which platform you\'re advertising on and what your campaign goal is — brand awareness, lead generation, website traffic, app installs, or direct sales. AdForge automatically adjusts its output to match each platform\'s format requirements, tone expectations, and character limits.',
    tips: [
      'Facebook and Instagram ads work best with emotional hooks and strong visuals',
      'Google Ads need keyword-rich headlines and benefit-focused descriptions',
      'TikTok ads should be conversational, fast-paced, and scroll-stopping',
    ],
    image: 'https://storage.readdy-site.link/project_files/b82d3669-b3d5-44e8-8c89-634acf8d0274/bce28386-40c7-479b-9731-e498fc7dbeb4_platform_campaign_img.png?v=6ef15099fba0ffe5da2e8250d425f3b0',
    color: 'amber',
    accent: 'bg-amber-50 text-amber-500 ring-amber-200',
  },
  {
    number: '03',
    icon: 'ri-sparkling-2-line',
    title: 'AI Generates Your Complete Ad in Seconds',
    subtitle: 'Copy + image + formatted for your platform',
    description:
      'Hit generate. In under 30 seconds, AdForge\'s AI creates a complete, launch-ready ad: a scroll-stopping hook, compelling headline, persuasive body copy, strong CTA, and a matching AI-generated creative image — all formatted to your platform\'s exact specs. Multiple variations are generated simultaneously for testing.',
    tips: [
      'Each variation uses a different psychological angle — compare them before picking',
      'The AI image matches your ad\'s offer and brand automatically',
      'Regenerate individual variations you don\'t like without losing the rest',
    ],
    image: 'https://storage.readdy-site.link/project_files/b82d3669-b3d5-44e8-8c89-634acf8d0274/94593c82-993e-4313-92d6-3efd5ccad335_adForgeImage2.jpg?v=0fcac2def287f2555b03596f814b4a19',
    color: 'rose',
    accent: 'bg-rose-50 text-rose-500 ring-rose-200',
  },
  {
    number: '04',
    icon: 'ri-edit-2-line',
    title: 'Review, Edit & Perfect Your Ad',
    subtitle: 'The visual editor makes tweaks effortless',
    description:
      'Review your AI-generated ads and make any adjustments in the built-in visual editor. Swap a headline, tweak the image, change the CTA button color, reposition elements — it\'s all drag-and-drop. Most users spend less than 5 minutes editing before their ad is perfect.',
    tips: [
      'Try the ad copy on a colleague or friend — gut reactions tell you a lot',
      'Make sure your offer is crystal clear in the headline',
      'Check that the image supports the copy — they should tell the same story',
    ],
    image: 'https://readdy.ai/api/search-image?query=person%20editing%20an%20advertisement%20in%20a%20clean%20drag-and-drop%20visual%20design%20editor%20on%20a%20large%20crisp%20widescreen%20monitor%2C%20the%20ad%20on%20screen%20clearly%20shows%20English%20text%20headline%20reading%20Shop%20Now%20and%20a%20bright%20product%20image%20with%20a%20call%20to%20action%20button%2C%20modern%20minimal%20UX%20design%20tool%20interface%2C%20warm%20bright%20office%20with%20natural%20window%20light%2C%20sharp%20screen%20with%20no%20glare&width=680&height=420&seq=hiw-step4-v5&orientation=landscape',
    color: 'teal',
    accent: 'bg-teal-50 text-teal-500 ring-teal-200',
  },
  {
    number: '05',
    icon: 'ri-rocket-line',
    title: 'Export & Launch Your Ad',
    subtitle: 'Ready for any ad manager in one click',
    description:
      'Export your finished ad creative at platform-perfect dimensions. Copy the ad text to your clipboard and paste it directly into Facebook Ads Manager, Google Ads, or your platform of choice. Download the image in the correct format and you\'re live. Start to launch in under 3 minutes.',
    tips: [
      'Save your best ads to the library — they\'re great reference for future campaigns',
      'Run 2–3 variations at once to find your winner fast',
      'Monitor CTR in your ad manager and regenerate a new batch if needed',
    ],
    image: 'https://readdy.ai/api/search-image?query=entrepreneur%20successfully%20launching%20a%20digital%20advertising%20campaign%20on%20a%20laptop%20and%20phone%2C%20showing%20ads%20going%20live%20on%20social%20media%20platforms%2C%20celebratory%20productive%20mood%2C%20modern%20bright%20home%20office%2C%20warm%20natural%20light%2C%20satisfied%20expression&width=680&height=420&seq=hiw-step5-01&orientation=landscape',
    color: 'emerald',
    accent: 'bg-emerald-50 text-emerald-500 ring-emerald-200',
  },
];

export default function HIWSteps() {
  return (
    <section id="steps" className="py-20 lg:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-xs font-bold uppercase tracking-widest text-orange-500 mb-3 block">Full Walkthrough</span>
          <h2 className="text-3xl lg:text-4xl font-black text-stone-900 mb-4 leading-tight">
            The Complete Step-by-Step Process
          </h2>
          <p className="text-stone-500 text-base leading-relaxed">
            Here&apos;s exactly how creating ads with AI works in AdForge — every step explained
            in plain English for complete beginners.
          </p>
        </div>

        <div className="space-y-16">
          {steps.map((step, idx) => (
            <article
              key={step.number}
              className={`flex flex-col ${idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-10 lg:gap-14 items-center`}
            >
              {/* Image */}
              <div className="w-full lg:w-[45%] flex-shrink-0 rounded-2xl overflow-hidden border border-stone-100 h-60 lg:h-72">
                <img
                  src={step.image}
                  alt={`Step ${step.number}: ${step.title}`}
                  title={`How to create ads with AI — Step ${step.number}`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Content */}
              <div className="flex-1">
                <div className={`inline-flex items-center gap-3 mb-4`}>
                  <div className={`w-12 h-12 flex flex-col items-center justify-center rounded-2xl ring-4 ${step.accent}`}>
                    <span className="text-xs font-black leading-none">{step.number}</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-stone-900 leading-snug">{step.title}</h3>
                    <p className="text-xs font-semibold text-stone-400 mt-0.5">{step.subtitle}</p>
                  </div>
                </div>

                <p className="text-sm text-stone-500 leading-relaxed mb-5">{step.description}</p>

                <div className="bg-stone-50 rounded-xl p-4 space-y-2.5">
                  <p className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-1">Pro tips:</p>
                  {step.tips.map((tip) => (
                    <div key={tip} className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-lightbulb-flash-line text-orange-400 text-sm" />
                      </div>
                      <span className="text-xs text-stone-500 leading-relaxed">{tip}</span>
                    </div>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-16 text-center">
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            Start Creating Ads Now — Free
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}
